import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { WEEKLY_PAYOUT_PAGES_ENABLED, WEEKLY_PAYOUT_HISTORY_ENABLED } from "@/lib/features";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { RealtimeProvider } from "@/lib/realtime";
import "@/index.css";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Plan from "@/pages/Plan";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Grievance from "@/pages/Grievance";
import Packages from "@/pages/Packages";
import Legal from "@/pages/Legal";
import LegalDocuments from "@/pages/LegalDocuments";
import Courses from "@/pages/Courses";
import Rewards from "@/pages/Rewards";
import GetAssociated from "@/pages/GetAssociated";
import OffersPage from "@/pages/OffersPage";
import TeamAchievers from "@/pages/TeamAchievers";

import DistDashboard from "@/pages/dist/DashboardPro";
import DistTree from "@/pages/dist/Tree";
import DistWallet from "@/pages/dist/Wallet";
import DistIncome from "@/pages/dist/Income";
import DistOrders from "@/pages/dist/Orders";
import DistKyc from "@/pages/dist/Kyc";
import DistReferral from "@/pages/dist/Referral";
import DistPayouts from "@/pages/dist/Payouts";
import DistDownline from "@/pages/dist/Downline";
import DistAllDownline from "@/pages/dist/AllDownline";
import DistRewards from "@/pages/dist/Rewards";
import DistBank from "@/pages/dist/Bank";
import DistPasswordReset from "@/pages/dist/PasswordReset";
import DistProfile from "@/pages/dist/Profile";
import DistMonthlyBonus from "@/pages/dist/MonthlyBonus";
import DistWelcome from "@/pages/dist/Welcome";
import DistIdCard from "@/pages/dist/IdCard";
import DistWeeklySales from "@/pages/dist/WeeklySales";
import DistSupport from "@/pages/dist/Support";
import DistShop from "@/pages/dist/Shop";
import DistWeeklyPayoutHistory from "@/pages/dist/WeeklyPayoutHistory";
import DistAccountInfo from "@/pages/dist/AccountInfo";
import AdminDocs from "@/pages/admin/AdminDocs";
import AdminWeeklyPayoutHistory from "@/pages/admin/WeeklyPayoutHistory";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminMembers from "@/pages/admin/Members";
import AdminMemberDetail from "@/pages/admin/MemberDetail";
import AdminBankDetails from "@/pages/admin/BankDetails";
import AdminSalesLedger from "@/pages/admin/SalesLedger";
import AdminProducts from "@/pages/admin/Products";
import AdminOrders from "@/pages/admin/Orders";
import AdminKyc from "@/pages/admin/Kyc";
import AdminPayouts from "@/pages/admin/Payouts";
import AdminPayments from "@/pages/admin/Payments";
import AdminNotices from "@/pages/admin/Notices";
import AdminGrievances from "@/pages/admin/Grievances";
import AdminReports from "@/pages/admin/Reports";
import AdminPlacementMoves from "@/pages/admin/PlacementMoves";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminGlossary from "@/pages/admin/Glossary";
import SuperadminDashboard from "@/pages/superadmin/Dashboard";
import SuperadminBanners from "@/pages/superadmin/Banners";
import SmartOpportunity from "@/pages/SmartOpportunity";
import Gallery from "@/pages/Gallery";

import FranchisePos from "@/pages/franchise/Pos";
import FranchiseDashboard from "@/pages/franchise/Dashboard";

import ProtectedRoute from "@/components/ProtectedRoute";
import PWAInstall from "@/components/PWAInstall";
import ChatBot from "@/components/ChatBot";
import FloatingCredit from "@/components/FloatingCredit";
import ImpersonationBanner from "@/components/ImpersonationBanner";

function App() {
  return (
    <AuthProvider>
      <RealtimeProvider>
        <CartProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <PWAInstall />
          <ChatBot />
          <FloatingCredit />
          <ImpersonationBanner />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/opportunity" element={<SmartOpportunity />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/plan" element={<ProtectedRoute><Plan /></ProtectedRoute>} />
            <Route path="/products" element={<Products />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/grievance" element={<Grievance />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/legal/documents" element={<LegalDocuments />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/rewards" element={<Rewards />} />
            <Route path="/get-associated" element={<GetAssociated />} />
            <Route path="/offers" element={<OffersPage />} />
            <Route path="/team-achievers" element={<TeamAchievers />} />

            {/* IBA */}
            <Route path="/dashboard" element={<ProtectedRoute roles={["distributor","customer","franchise","admin"]}><DistDashboard /></ProtectedRoute>} />
            <Route path="/tree" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistTree /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><DistWallet /></ProtectedRoute>} />
            <Route path="/income" element={WEEKLY_PAYOUT_PAGES_ENABLED ? <ProtectedRoute roles={["distributor","admin","franchise"]}><DistIncome /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
            <Route path="/my-orders" element={<ProtectedRoute><DistOrders /></ProtectedRoute>} />
            <Route path="/kyc" element={<ProtectedRoute><DistKyc /></ProtectedRoute>} />
            <Route path="/referral" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistReferral /></ProtectedRoute>} />
            <Route path="/payouts" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistPayouts /></ProtectedRoute>} />
            <Route path="/downline" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistDownline /></ProtectedRoute>} />
            <Route path="/all-downline" element={<ProtectedRoute roles={["distributor","admin","franchise","superadmin"]}><DistAllDownline /></ProtectedRoute>} />
            <Route path="/rewards" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistRewards /></ProtectedRoute>} />
            <Route path="/bank" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistBank /></ProtectedRoute>} />
            <Route path="/password-reset" element={<ProtectedRoute><DistPasswordReset /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><DistProfile /></ProtectedRoute>} />
            <Route path="/monthly-bonus" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistMonthlyBonus /></ProtectedRoute>} />
            <Route path="/welcome" element={<ProtectedRoute><DistWelcome /></ProtectedRoute>} />
            <Route path="/id-card" element={<ProtectedRoute><DistIdCard /></ProtectedRoute>} />
            <Route path="/weekly-sales" element={<ProtectedRoute roles={["distributor","admin","franchise"]}><DistWeeklySales /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><DistSupport /></ProtectedRoute>} />
            <Route path="/shop" element={<ProtectedRoute><DistShop /></ProtectedRoute>} />
            <Route path="/weekly-payouts" element={WEEKLY_PAYOUT_HISTORY_ENABLED ? <ProtectedRoute roles={["distributor","admin","franchise"]}><DistWeeklyPayoutHistory /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
            <Route path="/account-info" element={<ProtectedRoute roles={["distributor","admin","franchise","superadmin"]}><DistAccountInfo /></ProtectedRoute>} />
            <Route path="/admin/docs" element={<ProtectedRoute roles={["admin"]}><AdminDocs /></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/members" element={<ProtectedRoute roles={["admin"]}><AdminMembers /></ProtectedRoute>} />
            <Route path="/admin/members/:user_code" element={<ProtectedRoute roles={["admin"]}><AdminMemberDetail /></ProtectedRoute>} />
            <Route path="/admin/bank-details" element={<ProtectedRoute roles={["admin"]}><AdminBankDetails /></ProtectedRoute>} />
            <Route path="/admin/sales-ledger" element={<ProtectedRoute roles={["admin"]}><AdminSalesLedger /></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute roles={["admin"]}><AdminProducts /></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute roles={["admin"]}><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/kyc" element={<ProtectedRoute roles={["admin"]}><AdminKyc /></ProtectedRoute>} />
            <Route path="/admin/payouts" element={<ProtectedRoute roles={["admin"]}><AdminPayouts /></ProtectedRoute>} />
            <Route path="/admin/weekly-payouts" element={<ProtectedRoute roles={["admin"]}><AdminWeeklyPayoutHistory /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute roles={["admin"]}><AdminPayments /></ProtectedRoute>} />
            <Route path="/admin/notices" element={<ProtectedRoute roles={["admin"]}><AdminNotices /></ProtectedRoute>} />
            <Route path="/admin/grievances" element={<ProtectedRoute roles={["admin"]}><AdminGrievances /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute roles={["admin"]}><AdminReports /></ProtectedRoute>} />
            <Route path="/admin/placement-moves" element={<ProtectedRoute roles={["admin"]}><AdminPlacementMoves /></ProtectedRoute>} />
            <Route path="/admin/audit-log" element={<ProtectedRoute roles={["admin"]}><AdminAuditLog /></ProtectedRoute>} />
            <Route path="/admin/glossary" element={<ProtectedRoute roles={["admin"]}><AdminGlossary /></ProtectedRoute>} />

            {/* Superadmin */}
            <Route path="/superadmin" element={<ProtectedRoute roles={["superadmin"]}><SuperadminDashboard /></ProtectedRoute>} />
            <Route path="/superadmin/banners" element={<ProtectedRoute roles={["superadmin"]}><SuperadminBanners /></ProtectedRoute>} />

            {/* Franchise */}
            <Route path="/franchise" element={<ProtectedRoute roles={["franchise","admin"]}><FranchiseDashboard /></ProtectedRoute>} />
            <Route path="/franchise/pos" element={<ProtectedRoute roles={["franchise","admin"]}><FranchisePos /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </CartProvider>
      </RealtimeProvider>
    </AuthProvider>
  );
}

export default App;
